import { LitElement, html, css, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'

@customElement('coverage-selection')
export class CoverageSelection extends LitElement {
  static styles = css`
    :host {
      display: block;
    }
    .required-star {
      color: var(--lh-color-danger-600, #cc393b);
    }
    .vision-select-warning {
      display: flex;
      flex-direction: row;
      align-items: center;
      border-radius: 8px;
      gap: 16px;
      padding-bottom: 24px;
      padding-left: 24px;
      padding-right: 24px;
      background: var(--Primary-300, #5ecfff);
    }
    .icon-top-padding {
      position: relative;
      top: 14px;
    }
  `

  @property({ type: Boolean })
  dentalSelected = false

  @property({ type: Boolean })
  visionSelected = false

  @property({ type: Boolean })
  isAgent = false

  @property({ type: String })
  deliveryMethod: 'e' | 'p' | '' = ''

  @state()
  private visionAlertSelected = false
  updated(changedProps: Map<string, any>) {
    super.updated?.(changedProps)
    // Sync visionAlertSelected when visionSelected property changes
    if (changedProps.has('visionSelected')) {
      this.visionAlertSelected = this.visionSelected
    }
  }

  private handleCoverageChange(event: CustomEvent) {
    const path = event.composedPath()
    const checkboxCard = path.find(
      (el) => (el as HTMLElement).tagName?.toLowerCase() === 'lh-checkbox-card'
    ) as HTMLElement

    if (checkboxCard) {
      const value = checkboxCard.getAttribute('value')
      const checked = (event as any).detail?.checked

      if (value === 'dental') {
        this.dispatchEvent(
          new CustomEvent('dental-change', {
            detail: { checked },
            bubbles: true,
            composed: true,
          })
        )
      } else if (value === 'vision') {
        this.dispatchEvent(
          new CustomEvent('vision-change', {
            detail: { checked },
            bubbles: true,
            composed: true,
          })
        )
      }
    }
  }

  connectedCallback() {
    super.connectedCallback()
    this.addEventListener(
      'lh-change',
      this.handleCoverageChange as EventListener
    )
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListener(
      'lh-change',
      this.handleCoverageChange as EventListener
    )
  }

  private quoteDeliveryMethodChange = (event: CustomEvent) => {
    const value = (event as any).detail?.value
    this.dispatchEvent(
      new CustomEvent('quote-delivery-method-change', {
        detail: { value },
        bubbles: true,
        composed: true,
      })
    )
  }

  render() {
    return html`
      ${this.isAgent
        ? html`<lh-container gutters="none" context-id="coverage-selection"
            ><lh-radio-group
              required
              name="quote-delivery-method"
              default-value=${this.deliveryMethod === 'p'
                ? 'agent-pabol'
                : this.deliveryMethod === 'e'
                  ? 'agent-email'
                  : ''}
              @lh-change=${this.quoteDeliveryMethodChange}
            >
              <lh-group-legend slot="legend"
                >Select quote delivery method</lh-group-legend
              >
              <lh-radio value="agent-pabol">Agent submitted – PABOL</lh-radio>
              <lh-radio value="agent-email"
                >Applicant submitted – Rate Quote delivered by e-mail</lh-radio
              >
            </lh-radio-group></lh-container
          >`
        : nothing}
      <lh-typography size="text-md">
        <strong>
          Select the coverage you'd like to quote
          <span class="required-star">*</span>
        </strong>
      </lh-typography>
      <lh-grid>
        <lh-grid-item>
          <lh-checkbox-card
            name="checkbox-card-name"
            value="dental"
            layout="horizontal"
            data-testid="dental-checkbox"
            ?default-checked=${this.dentalSelected}
          >
            <lh-icon slot="media" name="tooth"></lh-icon>
            <div slot="content">
              <lh-typography><strong>Dental</strong></lh-typography>
              <lh-typography size="text-md">3 plan options</lh-typography>
              <lh-typography size="text-xs">
                Starting at $23.86 per month
              </lh-typography>
            </div>
          </lh-checkbox-card>
        </lh-grid-item>
        <lh-grid-item>
          <lh-checkbox-card
            name="checkbox-card-name"
            value="vision"
            layout="horizontal"
            ?default-checked=${this.visionSelected}
          >
            <lh-icon slot="media" name="eyeglasses"></lh-icon>
            <div slot="content">
              <lh-typography><strong>Vision</strong></lh-typography>
              <lh-typography size="text-md">2 plan options</lh-typography>
              <lh-typography size="text-xs">
                Starting at $7.16 per month
              </lh-typography>
            </div>
          </lh-checkbox-card>
        </lh-grid-item>
      </lh-grid>
      ${this.visionAlertSelected && !this.isAgent
        ? html` <lh-container gutters="none" margin-top="lg">
            <div class="vision-select-warning">
              <div class="icon-top-padding">
                <lh-icon name="bell"></lh-icon>
              </div>
              <lh-typography size="text-lg">
                Applicants who had Blue 20/20 for Individuals coverage within
                the past 12 months are not eligible to re-enroll, unless
                coverage is for a newly adopted or foster child, or required by
                court order.
              </lh-typography>
            </div>
          </lh-container>`
        : ''}
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'coverage-selection': CoverageSelection
  }
}
